import { useEffect, useRef } from 'react'

import gsap from 'gsap'

import type { FC } from 'react'

export interface AnimatedIndicatorProps {
  isActive: boolean
  className?: string
  style?: React.CSSProperties
}

const ANIMATION_DURATION = 0.3
const SCORE_ANIMATION_TOTAL = 0.6 // Score fade-out (0.3s) + fade-in (0.3s)

const AnimatedIndicator: FC<AnimatedIndicatorProps> = ({ isActive, className, style }) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const previousActiveRef = useRef<boolean | null>(null)
  const isFirstRenderRef = useRef(true)
  const tweenRef = useRef<gsap.core.Tween | null>(null)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // On first render, set initial opacity without animation
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      previousActiveRef.current = isActive
      gsap.set(element, { opacity: isActive ? 1 : 0 })
      return
    }

    // Only animate if value actually changed
    if (previousActiveRef.current === isActive) {
      return
    }

    previousActiveRef.current = isActive

    // Kill any existing animation
    if (tweenRef.current) {
      tweenRef.current.kill()
    }

    // Fade out: happens immediately (before score animation)
    // Fade in: happens after score animation completes
    const delay = isActive ? SCORE_ANIMATION_TOTAL : 0

    tweenRef.current = gsap.to(element, {
      opacity: isActive ? 1 : 0,
      duration: ANIMATION_DURATION,
      delay,
      ease: 'power2.inOut',
    })

    return () => {
      if (tweenRef.current) {
        tweenRef.current.kill()
      }
    }
  }, [isActive])

  return <div ref={elementRef} className={className} style={style} />
}

export default AnimatedIndicator
